/**
 * System Status - Elegant, minimal monitoring
 */

import { cn } from '../../lib/utils'

interface Service {
  name: string
  status: 'online' | 'degraded' | 'offline'
  latency?: number
}

const SERVICES: Service[] = [
  { name: 'API Gateway', status: 'online', latency: 45 },
  { name: 'Auth Service', status: 'online', latency: 32 },
  { name: 'Memory Core', status: 'online', latency: 28 },
  { name: 'MATRIZ Engine', status: 'online', latency: 156 },
  { name: 'Guardian', status: 'degraded', latency: 89 },
  { name: 'Compute', status: 'online', latency: 12 },
]

const STARS = [
  { name: 'Identity', emoji: '⚛️', active: true },
  { name: 'Memory', emoji: '✦', active: true },
  { name: 'Vision', emoji: '🔬', active: true },
  { name: 'Bio', emoji: '🌱', active: false },
  { name: 'Dream', emoji: '🌙', active: false },
  { name: 'Ethics', emoji: '⚖️', active: true },
  { name: 'Guardian', emoji: '🛡️', active: true },
  { name: 'Quantum', emoji: '⚛️', active: false },
]

export default function SystemStatus() {
  const allOnline = SERVICES.every(s => s.status === 'online')
  const avgLatency = Math.round(SERVICES.reduce((acc, s) => acc + (s.latency || 0), 0) / SERVICES.length)

  return (
    <div className="space-y-16">
      {/* Header with overall status */}
      <div className="text-center">
        <div className={cn(
          'inline-flex items-center gap-3 px-4 py-2 rounded-full mb-6',
          allOnline ? 'bg-emerald-500/10' : 'bg-yellow-500/10'
        )}>
          <span className={cn(
            'w-2 h-2 rounded-full',
            allOnline ? 'bg-emerald-500' : 'bg-yellow-500'
          )} />
          <span className={cn(
            'text-sm',
            allOnline ? 'text-emerald-400' : 'text-yellow-400'
          )}>
            {allOnline ? 'All systems operational' : 'Partial degradation'}
          </span>
        </div>

        <h1 className="text-2xl font-light text-white mb-2">System Status</h1>
        <p className="text-gray-500">Average response time: {avgLatency}ms</p>
      </div>

      {/* Services */}
      <div className="space-y-2">
        {SERVICES.map(service => (
          <div
            key={service.name}
            className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
          >
            <div className="flex items-center gap-3">
              <span className={cn(
                'w-2 h-2 rounded-full',
                service.status === 'online' && 'bg-emerald-500',
                service.status === 'degraded' && 'bg-yellow-500',
                service.status === 'offline' && 'bg-red-500'
              )} />
              <span className="text-white">{service.name}</span>
            </div>
            {service.latency && (
              <span className="text-sm text-gray-500">{service.latency}ms</span>
            )}
          </div>
        ))}
      </div>

      {/* Constellation */}
      <div>
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">
          Constellation Framework
        </h2>
        <div className="flex justify-center gap-6">
          {STARS.map(star => (
            <div key={star.name} className="text-center group">
              <div className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center text-xl mb-2 transition-all',
                star.active
                  ? 'bg-white/10 ring-1 ring-white/20'
                  : 'bg-white/5 opacity-40'
              )}>
                {star.emoji}
              </div>
              <span className={cn(
                'text-xs',
                star.active ? 'text-gray-400' : 'text-gray-600'
              )}>
                {star.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
